import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { XMLParser } from 'fast-xml-parser';

import {
  CancelListResponse,
  ExportDetailedInventoryResult,
  ExportInventoryCorrectionResult,
  ExportInventoryResult,
  ExportListResult,
  ImportItem,
  ImportList,
  SoapConfig
} from './icam-soap.models';

@Injectable({
  providedIn: 'root'
})
export class IcamSoapService {

  private config: SoapConfig = {
    endpoint: 'http://localhost:3000/servizi_xchange.asmx',
    namespace: 'http://servizi_xchange.org/'
  };

  constructor(private http: HttpClient) {}

  /* =========================================================
      GENERIC SOAP CALL
  ========================================================= */

  private async soapCall(
    method: string,
    bodyContent: string,
    ip:string,
    port:number
  ): Promise<any> {

    const soapEnvelope = `<?xml version="1.0" encoding="utf-8"?>
      <soap:Envelope
        xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
        xmlns:xsd="http://www.w3.org/2001/XMLSchema"
        xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">

        <soap:Body>
          <${method} xmlns="${this.config.namespace}">
            ${bodyContent}
          </${method}>
        </soap:Body>

      </soap:Envelope>
    `;

    const headers = new HttpHeaders({
      'Content-Type': 'text/xml; charset=utf-8',
      SOAPAction: `${this.config.namespace}${method}`
    });

    const response = await
      this.http.post(
        `http://${ip ?? 'localhost'}:${port ?? 80}/servizi_xchange.asmx`,
        soapEnvelope,
        {
          headers,
          responseType: 'text'
        }
      ).toPromise();

    const parser = new XMLParser();

    const parsed = parser.parse(response);

    const body =
      parsed['soap:Envelope']['soap:Body'];

    const responseNode =
      body[`${method}Response`];

    const result =
      responseNode[`${method}Result`];

    /*
    |--------------------------------------------------------------------------
    | ICAM restituisce JSON serializzato come stringa
    |--------------------------------------------------------------------------
    */

    if (typeof result === 'string') {

      try {
        return JSON.parse(result);
      } catch {
        return result;
      }
    }

    return result;

  }

  /* =========================================================
      HELPERS
  ========================================================= */

  private tag(name: string, value: any): string {
    if (value === undefined || value === null) {
      return '';
    }

    return `<${name}>${value}</${name}>`;
  }

  /* =========================================================
      1. IMPORT ITEMS
  ========================================================= */

  async importItems(
    items: ImportItem[],
    ip: string,
    port: number,
    outputType = 1
  ): Promise<any> {

    const itemsXml = items.map(item => `
      <Item>
        ${this.tag('item_id', item.item_id)}
        ${this.tag('name', item.name)}
        ${this.tag('description', item.description)}
        ${this.tag('category', item.category)}
        ${this.tag('pickpolicy', item.pickpolicy)}
        ${this.tag('measureunity', item.measureunity)}
        ${this.tag('weight', item.weight)}
        ${this.tag('volume', item.volume)}
        ${this.tag('barcode', item.barcode)}
        ${this.tag('photo', item.photo)}
        ${this.tag('minimuninventory', item.minimuninventory)}
      </Item>
    `).join('');

    return this.soapCall(
      'ImportItems',
      `
      <Items>
        ${itemsXml}
      </Items>

      <Output_Type>${outputType}</Output_Type>
      `,
      ip,
      port
    );
  }

  /* =========================================================
      2. IMPORT LISTS
  ========================================================= */

  async importLists(
    lists: ImportList[],
    ip:string,
    port:number,
    outputType = 1
  ): Promise<any> {

    const listsXml = lists.map(list => {

      const details = list.details.map(d => `
        <Detail>
          ${this.tag('item_id', d.item_id)}
          ${this.tag('quantity', d.quantity)}
          ${this.tag('description', d.description)}
          ${this.tag('stock_id', d.stock_id)}
          ${this.tag('color', d.color)}
        </Detail>
      `).join('');

      return `
        <List>

          <Head>
            ${this.tag('external_list_code', list.head.external_list_code)}
            ${this.tag('operation_type', list.head.operation_type)}
            ${this.tag('operation', list.head.operation)}
            ${this.tag('name', list.head.name)}
            ${this.tag('description', list.head.description)}
            ${this.tag('user_id', list.head.user_id)}
            ${this.tag('store_id', list.head.store_id)}
            ${this.tag('workplace', list.head.workplace)}
          </Head>

          <Details>
            ${details}
          </Details>

        </List>
      `;
    }).join('');

    return this.soapCall(
      'ImportLists',
      `
      <Lists>
        ${listsXml}
      </Lists>

      <Output_Type>${outputType}</Output_Type>
      `,
      ip,
      port
    );
  }

  /* =========================================================
      3. MOVE LIST
  ========================================================= */

  async moveList(
    externalListCode: string,
    ip:string,
    port:number,
    outputType = 1
  ): Promise<any> {

    return this.soapCall(
      'MoveList',
      `
      <external_list_code>${externalListCode}</external_list_code>
      <Output_Type>${outputType}</Output_Type>
      `,
      ip,
      port
    );
  }

  /* =========================================================
      4. CANCEL LIST
  ========================================================= */

  async cancelList(
    listCode: string,
    ip:string,
    port:number,
    outputType = 1
  ): Promise<CancelListResponse> {

    return this.soapCall(
      'CancelList',
      `
      <ListCode>${listCode}</ListCode>
      <Output_Type>${outputType}</Output_Type>
      `,
      ip,
      port
    );
  }

  /* =========================================================
      5. EXPORT LISTS
  ========================================================= */

  async exportLists(
    ip:string,
    port:number,
    outputType = 1
  ): Promise<ExportListResult[]> {

    return this.soapCall(
      'ExportLists',
      `<Output_Type>${outputType}</Output_Type>`,
      ip,
      port
    );
  }

  /* =========================================================
      6. EXPORT ITEMS MOVEMENTS
  ========================================================= */

  async exportItemsMovements(
    ip:string,
    port:number,
    outputType = 1
  ): Promise<any[]> {

    return this.soapCall(
      'ExportItemMovement',
      `<Output_Type>${outputType}</Output_Type>`,
      ip,
      port
    );
  }

  /* =========================================================
      7. EXPORT INVENTORY
  ========================================================= */

  async exportInventory(
    ip:string,
    port:number,
    outputType = 1
  ): Promise<ExportInventoryResult[]> {

    return this.soapCall(
      'ExportInventory',
      `<Output_Type>${outputType}</Output_Type>`,
      ip,
      port
    );
  }

  /* =========================================================
      8. EXPORT DETAILED INVENTORY
  ========================================================= */

  async exportDetailedInventory(
    ip:string,
    port:number,
    itemsFilter = 0,
    outputType = 1
  ): Promise<ExportDetailedInventoryResult[]> {

    return this.soapCall(
      'ExportDetailedInventory',
      `
      <ItemsFilter>${itemsFilter}</ItemsFilter>
      <Output_Type>${outputType}</Output_Type>
      `,
      ip,
      port
    );
  }

  /* =========================================================
      9. EXPORT INVENTORY CORRECTION
  ========================================================= */

  async exportInventoryCorrection(
    ip:string,
    port:number,
    outputType = 1
  ): Promise<ExportInventoryCorrectionResult[]> {

    return this.soapCall(
      'ExportInventoryCorrection',
      `<Output_Type>${outputType}</Output_Type>`,
      ip,
      port
    );
  }

}
